import { useCheckoutState } from '@olo-web/client-state';
import { useIsSplitByAmount } from '@olo-web/utils/common/hooks/useIsSplitByAmount';
import { useOrder } from '../queries/useOrder';

type MoneyLike = string | number | null | undefined;

const parseMoney = (value: MoneyLike): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value !== 'string') return 0;

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toCents = (value: number): number => Math.round(value * 100);

const ceilToCents = (value: number): number => Math.ceil(value * 100);

const floorToCents = (value: number): number => Math.floor(value * 100);

const centsToMoneyString = (cents: number): string => (cents / 100).toFixed(2);

type OrderData = NonNullable<ReturnType<typeof useOrder>['data']>;
type Surcharge = OrderData['surcharges'][number];
type Tax = OrderData['displayTaxes'][number];
type Discount = OrderData['discounts'][number];

type AdjustmentLine =
  | {
      kind: 'surcharge';
      index: number;
      exactAmount: number;
      item: Surcharge;
    }
  | {
      kind: 'tax';
      index: number;
      exactAmount: number;
      item: Tax;
    }
  | {
      kind: 'discount';
      index: number;
      exactAmount: number;
      item: Discount;
    };

const allocateSignedAdjustmentsToTarget = (
  lines: AdjustmentLine[],
  targetTotalCents: number
): AdjustmentLine[] => {
  if (lines.length === 0) return [];

  const baseAllocations = lines.map((line, orderIndex) => {
    const exactCents = line.exactAmount * 100;
    const baseCents = exactCents >= 0 ? Math.floor(exactCents) : Math.ceil(exactCents);

    return {
      orderIndex,
      line,
      baseCents,
      remainder: exactCents - baseCents,
    };
  });

  const baseTotalCents = baseAllocations.reduce((sum, allocation) => sum + allocation.baseCents, 0);
  let centsToDistribute = targetTotalCents - baseTotalCents;
  const centsByLine = baseAllocations.map((allocation) => allocation.baseCents);

  if (centsToDistribute > 0) {
    const rankedUp = [...baseAllocations].sort((a, b) => {
      if (b.remainder !== a.remainder) return b.remainder - a.remainder;
      return a.orderIndex - b.orderIndex;
    });

    for (let i = 0; i < rankedUp.length && centsToDistribute > 0; i += 1) {
      centsByLine[rankedUp[i].orderIndex] += 1;
      centsToDistribute -= 1;
    }
  } else if (centsToDistribute < 0) {
    const rankedDown = [...baseAllocations].sort((a, b) => {
      if (a.remainder !== b.remainder) return a.remainder - b.remainder;
      return a.orderIndex - b.orderIndex;
    });

    for (let i = 0; i < rankedDown.length && centsToDistribute < 0; i += 1) {
      centsByLine[rankedDown[i].orderIndex] -= 1;
      centsToDistribute += 1;
    }
  }

  return baseAllocations.map((allocation, index) => ({
    ...allocation.line,
    exactAmount: (centsByLine[index] ?? 0) / 100,
  }));
};

const allocateProratedAdjustments = ({
  surcharges,
  taxes,
  discounts,
  ratio,
  cappedAdjustmentTotalCents,
}: {
  surcharges: Surcharge[];
  taxes: Tax[];
  discounts: Discount[];
  ratio: number;
  cappedAdjustmentTotalCents?: number;
}): {
  surcharges: Surcharge[];
  taxes: Tax[];
  discounts: Discount[];
  totalCents: number;
} => {
  const lines: AdjustmentLine[] = [
    ...surcharges.map(
      (item, index): AdjustmentLine => ({
        kind: 'surcharge',
        index,
        item,
        exactAmount: parseMoney(item.displayAmount) * ratio,
      })
    ),
    ...taxes.map(
      (item, index): AdjustmentLine => ({
        kind: 'tax',
        index,
        item,
        exactAmount: parseMoney(item.displayAmount) * ratio,
      })
    ),
    ...discounts.map(
      (item, index): AdjustmentLine => ({
        kind: 'discount',
        index,
        item,
        exactAmount: parseMoney(item.displayAmount) * ratio,
      })
    ),
  ];

  if (lines.length === 0 || ratio <= 0) {
    return {
      surcharges: surcharges.map((surcharge) => ({
        ...surcharge,
        displayAmount: '0.00',
      })),
      taxes: taxes.map((tax) => ({
        ...tax,
        displayAmount: '0.00',
      })),
      discounts: discounts.map((discount) => ({
        ...discount,
        displayAmount: '0.00',
      })),
      totalCents: 0,
    };
  }

  const exactAdjustmentTotal = lines.reduce((sum, line) => sum + line.exactAmount, 0);

  const uncappedTargetTotalCents =
    exactAdjustmentTotal >= 0
      ? ceilToCents(exactAdjustmentTotal)
      : floorToCents(exactAdjustmentTotal);

  const targetTotalCents =
    cappedAdjustmentTotalCents == null ? uncappedTargetTotalCents : cappedAdjustmentTotalCents;

  const allocatedLines = allocateSignedAdjustmentsToTarget(lines, targetTotalCents);

  const surchargeAmounts = new Map<number, string>();
  const taxAmounts = new Map<number, string>();
  const discountAmounts = new Map<number, string>();

  allocatedLines.forEach((line) => {
    const moneyString = centsToMoneyString(toCents(line.exactAmount));

    if (line.kind === 'surcharge') {
      surchargeAmounts.set(line.index, moneyString);
    } else if (line.kind === 'tax') {
      taxAmounts.set(line.index, moneyString);
    } else {
      discountAmounts.set(line.index, moneyString);
    }
  });

  return {
    surcharges: surcharges.map((surcharge, index) => ({
      ...surcharge,
      displayAmount: surchargeAmounts.get(index) ?? '0.00',
    })),
    taxes: taxes.map((tax, index) => ({
      ...tax,
      displayAmount: taxAmounts.get(index) ?? '0.00',
    })),
    discounts: discounts.map((discount, index) => ({
      ...discount,
      displayAmount: discountAmounts.get(index) ?? '0.00',
    })),
    totalCents: targetTotalCents,
  };
};

export type SelectedItemsCheckoutComputation = {
  balanceAdjustedForRemainingDue: boolean;
  total: number;
  itemTotal: number | null;
  surcharges: OrderData['surcharges'];
  taxes: OrderData['displayTaxes'];
  discounts: OrderData['discounts'];
};

export const computeSelectedItemsAmountAndAllocations = (
  order: NonNullable<OrderData>,
  selectedItemsAmount: number
): SelectedItemsCheckoutComputation => {
  const itemSubtotal = parseMoney(order?.itemSubtotal);
  const balanceDueAmountCents = toCents(parseMoney(order?.balanceDueAmount));
  const selectedItemsAmountCents = toCents(selectedItemsAmount);

  const ratio = itemSubtotal > 0 ? Math.min(selectedItemsAmount / itemSubtotal, 1) : 0;

  const uncappedAllocated = allocateProratedAdjustments({
    surcharges: order?.surcharges ?? [],
    taxes: order?.displayTaxes ?? [],
    discounts: order?.discounts ?? [],
    ratio,
  });

  const uncappedAmountCents = selectedItemsAmountCents + uncappedAllocated.totalCents;
  // Only treat as overpayment when clearly above balance due — not when equal within a few cents
  // (proration / rounding vs `balanceDueAmount` can differ by a penny).
  const BILL_UPDATE_BANNER_EPSILON_CENTS = 2;
  const balanceAdjustedForRemainingDue =
    uncappedAmountCents > balanceDueAmountCents + BILL_UPDATE_BANNER_EPSILON_CENTS;

  const cappedAmountCents = Math.min(uncappedAmountCents, balanceDueAmountCents);
  const cappedAdjustmentTotalCents = cappedAmountCents - selectedItemsAmountCents;

  const cappedAllocated = allocateProratedAdjustments({
    surcharges: order?.surcharges ?? [],
    taxes: order?.displayTaxes ?? [],
    discounts: order?.discounts ?? [],
    ratio,
    cappedAdjustmentTotalCents,
  });

  const chosenItemsAdjustmentLinesTotalCents = (() => {
    const discountCents =
      cappedAllocated.discounts?.reduce((sum, discount) => {
        const discountValue = discount?.discountValue;
        if (discountValue) return sum + toCents(+discountValue);
        return sum + toCents(parseMoney(discount?.displayAmount));
      }, 0) ?? 0;

    const surchargeCents =
      cappedAllocated.surcharges?.reduce((sum, surcharge) => {
        return sum + toCents(parseMoney(surcharge?.displayAmount));
      }, 0) ?? 0;

    const taxCents =
      cappedAllocated.taxes?.reduce((sum, tax) => {
        return sum + toCents(parseMoney(tax?.displayAmount));
      }, 0) ?? 0;

    return discountCents + surchargeCents + taxCents;
  })();

  const itemTotal = Number(
    ((cappedAmountCents - chosenItemsAdjustmentLinesTotalCents) / 100).toFixed(2)
  );

  return {
    balanceAdjustedForRemainingDue,
    total: cappedAmountCents / 100,
    itemTotal,
    surcharges: cappedAllocated.surcharges,
    taxes: cappedAllocated.taxes,
    discounts: cappedAllocated.discounts,
  };
};

export const getBalanceDueForCheckoutPayment = (
  order: IOrder | undefined,
  {
    guestBalance,
    selectedItemsAmount,
  }: { guestBalance: number | undefined; selectedItemsAmount: number | null },
  isSplitByAmount: boolean
): number | undefined => {
  if (!order) return undefined;
  if (selectedItemsAmount !== null) {
    return computeSelectedItemsAmountAndAllocations(order, selectedItemsAmount).total;
  }
  if (isSplitByAmount) {
    return guestBalance;
  }
  return undefined;
};

export const useCustomPaymentAmount = () => {
  const { guestBalance, selectedItemsAmount } = useCheckoutState();
  const isSplitByAmount = useIsSplitByAmount();
  const { data: order } = useOrder();

  let total: number | undefined;
  let itemTotal: number | null = null;
  let surcharges = order?.surcharges ?? [];
  let taxes = order?.displayTaxes ?? [];
  let discounts = order?.discounts ?? [];
  let selectedItemsExceedRemainingBalance = false;

  if (selectedItemsAmount !== null && order) {
    const result = computeSelectedItemsAmountAndAllocations(order, selectedItemsAmount);
    selectedItemsExceedRemainingBalance = result.balanceAdjustedForRemainingDue;
    surcharges = result.surcharges;
    taxes = result.taxes;
    discounts = result.discounts;
    total = result.total;
    itemTotal = result.itemTotal;
  } else if (isSplitByAmount) {
    total = guestBalance;
  }

  return {
    total,
    itemTotal,
    surcharges,
    taxes,
    discounts,
    selectedItemsExceedRemainingBalance,
  };
};
